import ErpContactsAPI from 'dashboard/api/erpContacts';
import { useConfig } from 'dashboard/composables/useConfig';

import types from '../mutation-types';

const TERMINAL_STATUSES = ['success', 'partial', 'failed'];

const terminalManualResult = ($state, requestId) => {
  if (!$state.manualRequests[requestId]) return null;

  const status = $state.events[requestId]?.status;
  return TERMINAL_STATUSES.includes(status) ? status : null;
};

export const state = () => ({
  records: {},
  selectedExternalIds: {},
  uiFlags: {},
  events: {},
  manualRequests: {},
  activeRequests: {},
  fetchGenerations: {},
});

export const getters = {
  getSnapshots: $state => contactId => $state.records[Number(contactId)] || [],
  getSelectedExternalId: $state => contactId =>
    $state.selectedExternalIds[Number(contactId)] || null,
  getSelectedSnapshot: ($state, $getters) => contactId => {
    const externalId = $getters.getSelectedExternalId(contactId);
    return (
      $getters
        .getSnapshots(contactId)
        .find(item => item.external_client_id === externalId) || null
    );
  },
  getUIFlags: $state => contactId =>
    $state.uiFlags[Number(contactId)] || {
      isFetching: false,
      isSyncing: false,
    },
};

export const actions = {
  async initialize({ dispatch }, payload) {
    if (!useConfig().erpProvider?.trim()) return;

    await dispatch('fetch', payload);
    await dispatch('sync', {
      ...payload,
      force: false,
      manual: false,
    });
  },
  async fetch({ commit, state: $state }, { conversationId, contactId }) {
    const id = Number(contactId);
    const generation = ($state.fetchGenerations[id] || 0) + 1;
    commit(types.SET_ERP_FETCH_GENERATION, { contactId, generation });
    commit(types.SET_ERP_CONTACT_UI_FLAG, {
      contactId,
      flags: { isFetching: true },
    });
    try {
      const { data } = await ErpContactsAPI.show(conversationId);
      if ($state.fetchGenerations[id] !== generation) {
        return $state.records[id] || [];
      }
      commit(types.SET_ERP_CONTACT, { contactId, snapshots: data.snapshots });
      return data.snapshots;
    } catch (_) {
      return null;
    } finally {
      if ($state.fetchGenerations[id] === generation) {
        commit(types.SET_ERP_CONTACT_UI_FLAG, {
          contactId,
          flags: { isFetching: false },
        });
      }
    }
  },
  async sync(
    { commit, state: $state },
    { conversationId, contactId, force, manual }
  ) {
    commit(types.SET_ERP_CONTACT_UI_FLAG, {
      contactId,
      flags: { isSyncing: true },
    });
    try {
      const { data } = await ErpContactsAPI.sync(conversationId, force);
      const terminalEvent = $state.events[data.request_id]?.status;
      const isPending =
        data.status === 'pending' && !TERMINAL_STATUSES.includes(terminalEvent);
      commit(types.SET_ERP_ACTIVE_REQUEST, {
        contactId,
        requestId: isPending ? data.request_id : null,
      });
      if (!isPending) {
        commit(types.SET_ERP_CONTACT_UI_FLAG, {
          contactId,
          flags: { isSyncing: false },
        });
      }
      if (manual && data.request_id) {
        commit(types.REGISTER_ERP_MANUAL_REQUEST, {
          requestId: data.request_id,
          contactId,
        });
      }
      const manualResult = manual
        ? terminalManualResult($state, data.request_id)
        : null;
      if (manualResult) {
        commit(types.CLEAR_ERP_MANUAL_REQUEST, data.request_id);
      }
      return {
        status: data.status,
        requestId: data.request_id,
        manualResult,
      };
    } catch (_) {
      commit(types.SET_ERP_ACTIVE_REQUEST, { contactId, requestId: null });
      commit(types.SET_ERP_CONTACT_UI_FLAG, {
        contactId,
        flags: { isSyncing: false },
      });
      return { status: 'request_failed', requestId: null };
    }
  },
  async handleSyncEvent(
    { commit, dispatch, rootGetters, state: $state },
    payload
  ) {
    commit(types.RECORD_ERP_SYNC_EVENT, payload);
    const manualResult = terminalManualResult($state, payload.request_id);
    if (manualResult) {
      commit(types.CLEAR_ERP_MANUAL_REQUEST, payload.request_id);
    }
    const selected = rootGetters.getSelectedChat;
    if (
      TERMINAL_STATUSES.includes(payload.status) &&
      selected?.meta?.sender?.id === payload.contact_id
    ) {
      await dispatch('fetch', {
        conversationId: selected.id,
        contactId: payload.contact_id,
      });
    }

    return { manualResult };
  },
};

export const mutations = {
  [types.SET_ERP_CONTACT]($state, { contactId, snapshots }) {
    const id = Number(contactId);
    const previousSnapshots = $state.records[id] || [];
    const selectedExternalId = $state.selectedExternalIds[id];
    const selectedExternalIds = { ...$state.selectedExternalIds };

    if (snapshots.length) {
      if (
        !snapshots.some(item => item.external_client_id === selectedExternalId)
      ) {
        const previousIndex = Math.max(
          previousSnapshots.findIndex(
            item => item.external_client_id === selectedExternalId
          ),
          0
        );
        const nextIndex = Math.min(previousIndex, snapshots.length - 1);
        selectedExternalIds[id] = snapshots[nextIndex].external_client_id;
      }
    } else {
      delete selectedExternalIds[id];
    }

    $state.records = {
      ...$state.records,
      [id]: snapshots,
    };
    $state.selectedExternalIds = selectedExternalIds;
    const pendingSnapshot = snapshots.find(
      snapshot => snapshot.sync_status === 'pending'
    );
    const requestId = pendingSnapshot?.sync_request_id;
    mutations[types.SET_ERP_ACTIVE_REQUEST]($state, { contactId, requestId });
    $state.uiFlags = {
      ...$state.uiFlags,
      [id]: {
        isFetching: false,
        ...$state.uiFlags[id],
        isSyncing: Boolean(pendingSnapshot),
      },
    };
  },
  [types.SET_ERP_SELECTED_SERVICE]($state, { contactId, externalClientId }) {
    const id = Number(contactId);
    const selectedExternalIds = { ...$state.selectedExternalIds };
    const snapshot = ($state.records[id] || []).find(
      item => item.external_client_id === externalClientId
    );

    if (snapshot) {
      selectedExternalIds[id] = snapshot.external_client_id;
    } else if (externalClientId == null) {
      delete selectedExternalIds[id];
    } else {
      return;
    }

    $state.selectedExternalIds = selectedExternalIds;
  },
  [types.SET_ERP_CONTACT_UI_FLAG]($state, { contactId, flags }) {
    const id = Number(contactId);
    $state.uiFlags = {
      ...$state.uiFlags,
      [id]: {
        isFetching: false,
        isSyncing: false,
        ...$state.uiFlags[id],
        ...flags,
      },
    };
  },
  [types.RECORD_ERP_SYNC_EVENT]($state, payload) {
    const id = Number(payload.contact_id);
    const previousStatus = $state.events[payload.request_id]?.status;
    const isRegressivePending =
      payload.status === 'pending' &&
      TERMINAL_STATUSES.includes(previousStatus);
    if (isRegressivePending) return;

    $state.events = {
      ...$state.events,
      [payload.request_id]: { status: payload.status, contactId: id },
    };
    const activeRequestId = $state.activeRequests[id];
    if (
      TERMINAL_STATUSES.includes(payload.status) &&
      activeRequestId &&
      activeRequestId !== payload.request_id
    ) {
      return;
    }

    mutations[types.SET_ERP_ACTIVE_REQUEST]($state, {
      contactId: id,
      requestId: payload.status === 'pending' ? payload.request_id : null,
    });
    $state.uiFlags = {
      ...$state.uiFlags,
      [id]: {
        isFetching: false,
        ...$state.uiFlags[id],
        isSyncing: payload.status === 'pending',
      },
    };
  },
  [types.SET_ERP_ACTIVE_REQUEST]($state, { contactId, requestId }) {
    const id = Number(contactId);
    const activeRequests = { ...$state.activeRequests };
    if (requestId) {
      activeRequests[id] = requestId;
    } else {
      delete activeRequests[id];
    }
    $state.activeRequests = activeRequests;
  },
  [types.SET_ERP_FETCH_GENERATION]($state, { contactId, generation }) {
    $state.fetchGenerations = {
      ...$state.fetchGenerations,
      [Number(contactId)]: generation,
    };
  },
  [types.REGISTER_ERP_MANUAL_REQUEST]($state, { requestId, contactId }) {
    $state.manualRequests = {
      ...$state.manualRequests,
      [requestId]: { contactId: Number(contactId) },
    };
  },
  [types.CLEAR_ERP_MANUAL_REQUEST]($state, requestId) {
    const { [requestId]: _request, ...manualRequests } = $state.manualRequests;
    $state.manualRequests = manualRequests;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
